import { components } from "@/api-schema";
import { RelocationOrderStatus } from "@/api-schema-enums";
import { UNLIMITED_PAGE_SIZE } from "@/common/api/_defaults";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { LocationDisplay } from "@/components/data-table/predefined/_helpers/relocation-order-location-display";
import { RelocationOrderTable } from "@/components/data-table/predefined/relocation-order-table";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { userStore } from "@/store/user-store";
import { TruckIcon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type RelocationOrderBody = components["schemas"]["RelocationOrderSearchBody"];

export function MyTruckView() {
  const { t } = useTranslation(["fit", "common"]);
  const { user } = userStore();

  const relocationOrderBaseBody: RelocationOrderBody = useMemo(
    () => ({
      status: [RelocationOrderStatus.TransportCompanyInTransit],
      pickedUpBy: [user.uuid],
    }),
    [user.uuid],
  );

  const mobileOptimizedColumns = useMemo(
    () => [
      {
        id: "vehicleAndRelocationOrderInfo",
        size: 1000,
        header: t("fit:VehicleAndOrderInformation"),
        enableSorting: false,
        cell: ({ row }) => {
          const vehicle = row.original.vehicle;
          const brand = vehicle?.modelGeneration?.modelFamily?.brand;
          const brandName = brand?.name;
          const modelGenerationName = vehicle?.modelGeneration?.name;

          return (
            <div className="flex min-w-0 flex-col gap-2 text-left">
              <div className="flex items-center gap-2 truncate text-base font-semibold">
                {brand && <BrandLogo brand={brand} variant={BrandLogoVariant.COMPACT} />}
                {(brandName || modelGenerationName) && (
                  <div className="truncate">{[brandName, modelGenerationName].filter(Boolean).join(" ")}</div>
                )}
              </div>

              <div className="text-muted-foreground flex flex-col gap-1 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">VIN:</span>
                  <span className="font-mono text-xs">{row.original.vehicle.vin}</span>
                </div>

                <div className="text-muted-foreground flex items-center gap-2">
                  <span className="font-medium">{t("common:Color")}:</span>
                  <span>{row.original.vehicle.color}</span>
                </div>

                <div className="flex items-center gap-2">
                  <span className="font-medium">{t("fit:Destination")}:</span>
                  <LocationDisplay
                    branch={row.original.destinationBranch}
                    address={row.original.destinationAddress}
                    className="text-foreground font-medium"
                  />
                </div>
              </div>
            </div>
          );
        },
      },
    ],
    [t],
  );

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={t("common:MyTruck")}
      subTitle={t("fit:MyTruckDescription")}
    >
      <RelocationOrderTable
        body={relocationOrderBaseBody}
        columns={mobileOptimizedColumns}
        pageSize={UNLIMITED_PAGE_SIZE}
        features={{
          pagination: false,
          search: true,
          multiSelection: false,
          expansion: true,
          hideExpansionButton: true,
        }}
        searchPlaceholder={t("fit:SearchVehicles")}
        noDataIcon={TruckIcon}
        noDataTitle={t("fit:NoVehiclesOnTruck")}
        noDataDescription={t("fit:NoVehiclesOnTruckDescription")}
        noResultsTitle={t("fit:NoVehiclesOnTruckMatchFilters")}
        noResultsDescription={t("fit:NoVehiclesOnTruckMatchFiltersDescription")}
      />
    </CenteredLayout>
  );
}
